import { ReactNode, useEffect, useMemo, useState } from "react";
import { ChevronLeft, Search } from "lucide-react";
import useSWR from "swr";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Skeleton, Table, Tabs, TabsProps, Popconfirm, message } from "antd"
import { API_ENDPOINTS, fetcher, fleetAPI } from "../../../util/api";
import { ToastAction } from "../../../components/ui/toast";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "../../../hooks/use-toast";
import { ColumnsType } from "antd/es/table";
import { cn } from "../../../lib/utils";
import ManageUser from "../../../componentsV2/ManageUser";
import ManagePartner from "../../../componentsV2/ManagePartner";
import ManageFleetOwners from "../../../componentsV2/ManageFleetOwners";
import CreatePartner from "../../../componentsV2/CreatePartner";
import { useCurrency } from "../../../hooks/useCurrency";
import axiosConfig from "../../../util/axiosConfig";
import axios from "axios";
import ManageBusinessRenter from "../../../componentsV2/ManageBusinessRenter";
import ManageReferralAgent from "../../../componentsV2/ManageReferralAgent";
import useTablePagination from "../../../hooks/useTablePagination";
import { fetcher as axiosFetcher } from "../../../util/axiosConfig";

type ActiveTab = 'renters' |
  'partners' |
  'fleet-owners' |
  'referral-agents' |
  'business-renters'

type ManageComponents = Record<ActiveTab, ReactNode>;

export default function FleetOwnersPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [selectedUser, setSelectedUser] = useState<Renter>();
  const [selectedBusinessRenter, setSelectedBusinessRenter] = useState<BusinessRenter>();
  const [selectedPartner, setSelectedPartner] = useState<Partner>();
  const [selectedFleetOwner, setSelectedFleetOwner] = useState<FleetOwner>();
  const [selectedReferralAgent, setSelectedReferralAgent] = useState<ReferralAgent>();
  const {formatCurrency} = useCurrency();
  const lastActiveTab = sessionStorage.getItem('active-user-tab') as ActiveTab;
  const [activeTab, setActiveTab] = useState<ActiveTab>(lastActiveTab || "renters");
  const navigate = useNavigate();
  const {pagination, setPagination} = useTablePagination("user-fleet-owners");
  const {pagination: referralPagination, setPagination: setReferralPagination} = useTablePagination("referral-agents");

  // Fetch data using SWR with axios fetcher
  const { data: fleetOwnerData, error, isLoading: loadingFleetOwners, mutate } = useSWR(
    `${API_ENDPOINTS.FLEET.ALL}?page=${pagination.current}&limit=${pagination.pageSize}`,
    axiosFetcher,
    {
      // revalidateOnFocus: false,
      // dedupingInterval: 60000, // 1 minute
      onError: (err) => {
        console.error("Error fetching fleet owners:", err);
        // You could also show a toast notification here
      },
    }
  );

  const fleetOwners: FleetOwner[] = useMemo(() => {
    if (!fleetOwnerData?.pagination) return [];
    setPagination((prev: any) => ({
      ...prev,
      total: fleetOwnerData.pagination.totalItems,
      pageSize: fleetOwnerData.pagination.itemsPerPage,
      // current: fleetOwnerData.pagination.currentPage
    }));
    return fleetOwnerData?.data || []
  }, [fleetOwnerData]);

  const { data: referralAgentsData, error: referralAgentsError, isLoading: loadingReferralAgents, mutate: mutateReferralAgents } = useSWR(
    `/referral/referral-agents?page=${referralPagination.current}&limit=${referralPagination.pageSize}`,
    axiosFetcher,
    {
      onError: (err) => {
        console.error("Error fetching referral agents:", err);
      },
    }
  );

  const referralAgents: ReferralAgent[] = useMemo(() => {
    if (!referralAgentsData?.pagination) return [];
    setReferralPagination((prev: any) => ({
      ...prev,
      total: referralAgentsData.pagination.totalRecords || referralAgentsData.pagination.total || 0,
      pageSize: referralAgentsData.pagination.limit || prev.pageSize,
    }));
    return referralAgentsData?.data || [];
  }, [referralAgentsData, setReferralPagination]);

  // Fetch data using SWR with axios fetcher
  const { data: renderData, error: renderError, isLoading: loadingRenters, mutate: mutateRenters } = useSWR(
    API_ENDPOINTS.RENTER.RENTERS,
    fetcher,
    {
      // revalidateOnFocus: false,
      // dedupingInterval: 60000, // 1 minute
      onError: (err) => {
        console.error("Error fetching Renters:", err);
        // You could also show a toast notification here
      },
    }
  );

  // Fetch data using SWR with axios fetcher
  const { data: businessRenderData, error: businessRenderError, isLoading: loadingBusinessRenters, mutate: mutateBusinessRenters } = useSWR(
    API_ENDPOINTS.RENTER.BUSINESS_RENTERS,
    fetcher,
    {
      // revalidateOnFocus: false,
      // dedupingInterval: 60000, // 1 minute
      onError: (err) => {
        console.error("Error fetching Business Renters:", err);
        // You could also show a toast notification here
      },
    }
  );
  // Fetch data using SWR with axios fetcher
  const { data: partnerData, error: partnerError, isLoading: loadingPartners, mutate: mutatePartner } = useSWR(
    API_ENDPOINTS.RENTER.PARTNERS,
    fetcher,
    {
      // revalidateOnFocus: false,
      // dedupingInterval: 60000, // 1 minute
      onError: (err) => {
        console.error("Error fetching Partners:", err);
        // You could also show a toast notification here
      },
    }
  );

  const filteredRenters: Renter[] = useMemo(() => {
    return renderData?.filter((renter: Renter) => renter.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      renter.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      renter.phone.toLowerCase().includes(searchQuery.toLowerCase())
    ) ?? []
  }, [businessRenderData, searchQuery])

  const filteredBusinessRenters: BusinessRenter[] = useMemo(() => {
    return businessRenderData?.filter((renter: BusinessRenter) => renter.companyName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      renter.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      renter.phone.toLowerCase().includes(searchQuery.toLowerCase())
    ) ?? []
  }, [businessRenderData, searchQuery])

  const filteredPartners: Partner[] = useMemo(() => {
    if (!partnerData) return [];
    return partnerData.filter((renter: Partner) => renter.businessName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      renter.email.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [partnerData, searchQuery])

  const filteredFleetOwners: FleetOwner[] = useMemo(() => {
    if (!fleetOwners) return [];
    return fleetOwners.sort((a, b) => a.approved == "REJECTED" ? -1 : 1).sort((a, b) => a.approved === "PENDING" ? -1 : 1)?.filter(
      (owner) =>
        owner.companyName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        owner.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        owner.approved?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        owner.phone?.includes(searchQuery)
    ) || [];
  }, [fleetOwners, searchQuery]);

  const filteredReferralAgents: ReferralAgent[] = useMemo(() => {
    if (!referralAgents) return [];
    return referralAgents.filter((agent) =>
      agent.fullName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      agent.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      agent.phone?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      agent.referralCode?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [referralAgents, searchQuery]);

  const handleClosePanel = () => {
    setSelectedUser(undefined)
  };

  const handleStatusUpdate = async (id: string, status: "active" | "inactive", userType: 'renter' | 'fleetowner' | 'businessRenter') => {
    try {
      const response = await axiosConfig.put(`/newAdmin/users/${userType}/${id}/toggle-status`,
      {
        status,
      })
      // Revalidate the data
     if (userType === 'renter') {
       mutateRenters();
        message.success(response.data.message || "Renter Status updated");
        return;
      }
      // Revalidate the data
     if (userType === 'businessRenter') {
       mutateBusinessRenters();
        message.success(response.data.message || "Business Renter Status updated");
        return;
      }

      mutate();
      message.success(response.data.message || "Status updated");
    } catch (error) {
      console.error("Error updating status:", error);
    }
  };

  const handleApprove = async (id: string) => {
    try {
      await fleetAPI.action(id, "APPROVED");

      // Show success toast
      toast({
        title: "Fleet owner approved",
        description: "The fleet owner has been successfully approved.",
        variant: "default",
      });

      // Revalidate the data
      mutate();
    } catch (error) {
      console.error("Error approving fleet owner:", error);

      // Show error toast
      toast({
        title: "Approval failed",
        description: axios.isAxiosError(error)
          ? error.response?.data?.message || error.message
          : "Failed to approve fleet owner. Please try again.",
        variant: "destructive",
        action: (
          <ToastAction altText="Try again" onClick={() => handleApprove(id)}>
            Try again
          </ToastAction>
        ),
      });
    }
  };

  // Handle disable action
  const handleDisable = async (id: string) => {
    try {
      await fleetAPI.action(id, "REJECTED");

      // Show success toast
      toast({
        title: "Fleet owner disabled",
        description: "The fleet owner has been successfully disabled.",
        variant: "default",
      });

      // Revalidate the data
      mutate();
    } catch (error) {
      console.error("Error disabling fleet owner:", error);

      // Show error toast
      toast({
        title: "Disable failed",
        description: axios.isAxiosError(error)
          ? error.response?.data?.message || error.message
          : "Failed to disable fleet owner. Please try again.",
        variant: "destructive",
        action: (
          <ToastAction altText="Try again" onClick={() => handleDisable(id)}>
            Try again
          </ToastAction>
        ),
      });
    }
  }

  const columns: ColumnsType<FleetOwner> = [
    {
      title: "Name",
      dataIndex: "companyName",
      key: "companyName",
      render: (_, record, index) => (
        <span>{record.companyName || "Fleet Owner Name"}</span>
      ),
    },
    // {
    //   title: "Company Reg",
    //   dataIndex: "companyRegistrationNumber",
    //   key: "companyRegistrationNumber",
    //   render: (_, record, index) => (
    //     <span className="">
    //       {record.companyRegistrationNumber || ""}
    //     </span>
    //   ),
    // },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (_, record, index) => (
        <span className="">
          {record.email || "N/A"}
        </span>
      ),
    },
    {
      title: "Phone Number",
      dataIndex: "phone",
      key: "phone",
      render: (_, record, index) => (
        <span>
          {record.phone || "N/A"}
        </span>
      ),
    },
    {
      title: "Address",
      dataIndex: "address",
      key: "address",
      render: (_, record, index) => (
        <span className="capitalize">
          {record.address || "N/A"}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "approved",
      key: "approved",
      render: (_, record, index) => (
        <>
        <span
          className={`
          ${record.approved.toLowerCase() === "approved"
              ? "text-primary"
              : ""
            }
          ${record.approved === "PENDING" ? "text-secondary" : ""
            }
          ${record.approved === "REJECTED"
              ? "text-secondary"
              : ""
            }
        capitalize`}
        >
          {record.approved.toLowerCase() || "Unknown"}
        </span>
        </>
      ),
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "operation",
      render: (_, record, index) => (
        <>
          {record.approved === "PENDING" ? (
            <div className="flex gap-2 ">
              <Button
                className="bg-blue-600 hover:bg-blue-700 text-white"
                onClick={(e) => {
                  e?.stopPropagation()
                  handleApprove(record.id)
                }}
              >
                Approve
              </Button>
              <Popconfirm
                title="Are you sure you want to reject this fleet owner?"
                onConfirm={(e) => {
                  e?.stopPropagation()
                  handleDisable(record.id)
                }}
              >
                <Button
                  variant="secondary"
                  onClick={(e) => {
                    e?.stopPropagation()
                    handleDisable(record.id)
                  }}
                >
                  Reject
                </Button>
              </Popconfirm>
            </div>
          ) : record.approved === "APPROVED" ? (
            <Button
              variant="secondary"
              onClick={(e) => {
                e?.stopPropagation()
                handleDisable(record.id)
              }}
            >
              Disable
            </Button>
          ) : <Button
            className="bg-blue-600 hover:bg-blue-700 text-white"
            onClick={(e) => {
              e?.stopPropagation();
              handleApprove(record.id);
            }}
          >
            Approve
          </Button>}
        </>
      ),
    },
  ]

  const renterColumns: ColumnsType<Renter> = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (_, record, index) => (
        <span>{record.name}</span>
      ),
    },
    {
      title: "Phone Number",
      dataIndex: "phone",
      key: "phone",
      render: (_, record, index) => (
        <span>
          {record.phone || "N/A"}
        </span>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (_, record, index) => (
        <span className="">
          {record.email || "N/A"}
        </span>
      ),
    },
    {
      title: "Address",
      dataIndex: "address",
      key: "address",
      render: (_, record, index) => (
        <span className="capitalize">
          {record.address || "N/A"}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (_, record, index) => (
        <span className="capitalize">
          {record.status || "N/A"}
        </span>
      ),
    },
    {
      title: "Action",
      dataIndex: 'action',
      render: (_, record, index) => (
        <div className="flex gap-2">
          {record.status !== 'active'
            ? <Button
                className="bg-blue-600 hover:bg-blue-700 uppercase font-bold text-white"
                onClick={(e) => {
                  e?.stopPropagation()
                  handleStatusUpdate(record.id, 'active', 'renter');
                }}
                >
                activate
              </Button>
            : <Popconfirm
                title="Are you sure you want to Deactivate this renter?"
                onConfirm={(e) => {
                  e?.stopPropagation()
                  handleStatusUpdate(record.id, 'inactive', 'renter');
                }}
                onCancel={(e) => {
                  e?.stopPropagation()
                }}
              >
                <Button
                  variant="secondary"
                  className="uppercase font-bold"
                  onClick={(e) => {
                    e?.stopPropagation();
                  }}
                >
                  deactivate
                </Button>
              </Popconfirm>
          }
        </div>
      ),
    },
  ]

  const businessRenterColumns: ColumnsType<BusinessRenter> = [
    {
      title: "Company Name",
      dataIndex: "companyName",
      key: "companyName",
      render: (_, record, index) => (
        <span>{record.companyName}</span>
      ),
    },
    {
      title: "Phone Number",
      dataIndex: "phone",
      key: "phone",
      render: (_, record, index) => (
        <span>
          {record.phone || "N/A"}
        </span>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (_, record, index) => (
        <span className="">
          {record.email || "N/A"}
        </span>
      ),
    },
    {
      title: "Address",
      dataIndex: "businessAddress",
      key: "businessAddress",
      render: (_, record, index) => (
        <span className="capitalize">
          {record.businessAddress || "N/A"}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (_, record, index) => (
        <span className="capitalize">
          {record.status || "N/A"}
        </span>
      ),
    },
    {
      title: "Action",
      dataIndex: 'action',
      render: (_, record, index) => (
        <div className="flex gap-2">
          {record.status !== 'active'
            ? <Button
                className="bg-blue-600 hover:bg-blue-700 uppercase font-bold text-white"
                onClick={(e) => {
                  e?.stopPropagation()
                  handleStatusUpdate(record.id, 'active', 'businessRenter');
                }}
                >
                activate
              </Button>
            : <Popconfirm
                title="Are you sure you want to Deactivate this renter?"
                onConfirm={(e) => {
                  e?.stopPropagation()
                  handleStatusUpdate(record.id, 'inactive', 'businessRenter');
                }}
                onCancel={(e) => {
                  e?.stopPropagation()
                }}
              >
                <Button
                  variant="secondary"
                  className="uppercase font-bold"
                  onClick={(e) => {
                    e?.stopPropagation();
                  }}
                >
                  deactivate
                </Button>
              </Popconfirm>
          }
        </div>
      ),
    },
  ]

  const partnerColumns: ColumnsType<Partner> = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (_, record, index) => (
        <span>{record.businessName}</span>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (_, record, index) => (
        <span className="">
          {record.email || "N/A"}
        </span>
      ),
    },
    {
      title: "Amount Owed",
      dataIndex: "totalAmountOwed",
      key: "totalAmountOwed",
      render: (_, record, index) => (
        <span className="">
          {formatCurrency(record.totalAmountOwed) || "N/A"}
        </span>
      ),
    },
    {
      title: "Limit",
      dataIndex: "limit",
      key: "limit",
      render: (_, record, index) => (
        <span className="">
          {formatCurrency(record.limit) || "N/A"}
        </span>
      ),
    },
    {
      title: "API Key",
      dataIndex: "apiKey",
      key: "apiKey",
      render: (_, record, index) => (
        <span className="">
          {record?.apiKey ? record?.apiKey?.slice(0, 6) + '...' : "N/A"}
        </span>
      ),
    },
    // {
    //   title: "Action",
    //   dataIndex: 'action',
    //   render: (_, record, index) => (
    //     <div className="flex gap-2">
    //       <Button
    //         className="bg-blue-600 hover:bg-blue-700 uppercase text-white font-bold"
    //         onClick={(e) => {
    //           e?.stopPropagation()
    //         }}
    //       >
    //         More
    //       </Button>
    //     </div>
    //   ),
    // },
  ]

  const referralAgentColumns: ColumnsType<ReferralAgent> = [
    {
      title: "Name",
      dataIndex: "fullName",
      key: "fullName",
      render: (_, record) => <span>{record.fullName || "N/A"}</span>,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (_, record) => <span>{record.email || "N/A"}</span>,
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      render: (_, record) => <span>{record.phone || "N/A"}</span>,
    },
    {
      title: "Referral Code",
      dataIndex: "referralCode",
      key: "referralCode",
      render: (_, record) => <span>{record.referralCode || "N/A"}</span>,
    },
    {
      title: "Total Referrals",
      dataIndex: "totalReferrals",
      key: "totalReferrals",
      render: (_, record) => <span>{record.totalReferrals ?? 0}</span>,
    },
    {
      title: "Earned Commission",
      dataIndex: "earnedCommission",
      key: "earnedCommission",
      render: (_, record) => <span>{formatCurrency(record.earnedCommission ?? 0)}</span>,
    },
    {
      title: "Pending",
      dataIndex: "pendingCommission",
      key: "pendingCommission",
      render: (_, record) => <span>{formatCurrency(record.pendingCommission ?? 0)}</span>,
    },
    {
      title: "Paid",
      dataIndex: "paidCommission",
      key: "paidCommission",
      render: (_, record) => <span>{formatCurrency(record.paidCommission ?? 0)}</span>,
    },
  ];

  const items: TabsProps["items"] = [
    {
      key: "renters",
      label: "Renters",
      children: loadingRenters ? <Skeleton active paragraph={{ rows: 8 }} /> : <Table
          columns={renterColumns}
          dataSource={filteredRenters}
          rowKey="id"
          onRow={(record) => {
            return {
              onClick: () => {
                setSelectedFleetOwner(undefined)
                setSelectedPartner(undefined)
                setSelectedBusinessRenter(undefined)
                setSelectedUser(record);
              },
            };
          }}
        />,
    },
    {
      key: "business-renters",
      label: "Business Renters",
      children: loadingBusinessRenters ? <Skeleton active paragraph={{ rows: 8 }} /> : <Table
          columns={businessRenterColumns}
          dataSource={filteredBusinessRenters}
          rowKey="id"
          onRow={(record) => {
            return {
              onClick: () => {
                setSelectedFleetOwner(undefined)
                setSelectedPartner(undefined)
                setSelectedUser(undefined);
                setSelectedBusinessRenter(record);
              },
            };
          }}
        />,
    },
    {
      key: "partners",
      label: "Partners",
      children: loadingFleetOwners ? <Skeleton active paragraph={{ rows: 8 }} /> : (<Table
          columns={partnerColumns}
          dataSource={filteredPartners}
          rowKey="id"
          // scroll={{ y: 55 * 5 }}
          onRow={(record) => {
            return {
              onClick: () => {
                setSelectedFleetOwner(undefined)
                setSelectedUser(undefined)
                setSelectedBusinessRenter(undefined)
                setShowCreate(false);
                setSelectedPartner(record);
              },
            };
          }}
        />),
    },
    {
      key: "referral-agents",
      label: "Referral Agents",
      children: loadingReferralAgents
        ? <Skeleton active paragraph={{ rows: 8 }} /> : (<Table
          columns={referralAgentColumns}
          dataSource={filteredReferralAgents}
          rowKey="id"
          pagination={{
            current: referralPagination.current,
            pageSize: referralPagination.pageSize,
            total: referralPagination.total,
            onChange: (page, pageSize) => setReferralPagination({
              ...referralPagination,
              current: page,
              pageSize: pageSize
            }),
          }}
          onRow={(record) => {
            return {
              onClick: () => {
                setSelectedUser(undefined)
                setSelectedBusinessRenter(undefined)
                setSelectedPartner(undefined)
                setSelectedFleetOwner(undefined)
                setSelectedReferralAgent(record);
              },
            };
          }}
        />),
    },
    {
      key: "fleet-owners",
      label: "Fleet Owners",
      children: loadingFleetOwners ? <Skeleton active paragraph={{ rows: 8 }} /> : (<Table
          columns={columns}
          dataSource={filteredFleetOwners}
          rowKey="id"
          // scroll={{ y: 66 * 9 }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            onChange: (page, pageSize) => setPagination({
              ...pagination,
              current: page,
              pageSize: pageSize
            }),
          }}
          onRow={(record) => {
            return {
              onClick: () => {
                setSelectedUser(undefined)
                setSelectedBusinessRenter(undefined)
                setSelectedPartner(undefined)
                setSelectedReferralAgent(undefined)
                setSelectedFleetOwner(record);
              },
            };
          }}
        />),
    }
  ];
  
  const SearchUsers = (<div className="relative w-full sm:w-auto hidden sm:block">
    <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
    <Input
      placeholder="Search"
      className="pl-8 w-full sm:w-[300px]"
      value={searchQuery}
      onChange={(e) => setSearchQuery(e.target.value)}
    />
  </div>);

  return (
    <div className={cn(
      "h-full grid grid-cols-1 gap-4 transition-all duration-300",
      selectedUser && "md:grid-cols-[1fr_350px]",
      selectedPartner && "md:grid-cols-[1fr_350px]",
      selectedFleetOwner && "md:grid-cols-[1fr_350px]",
      selectedBusinessRenter && "md:grid-cols-[1fr_350px]",
      selectedReferralAgent && "md:grid-cols-[1fr_350px]",
      showCreate && "md:grid-cols-[1fr_350px]"
    )}>
      <div className="bg-white rounded-md shadow-sm">
        {/* Header with back button and title */}
        <div className="p-6 flex items-center">
          <Button
            variant="ghost"
            className="text-primary mr-4"
            onClick={() => navigate(-1)}
          >
            <ChevronLeft size={24} />
          </Button>
          <h1 className="text-xl font-medium text-primary">Users</h1>
        </div>

        {/* Tabs and search */}
        <div className="flex flex-col gap-4 overflow-x-auto px-3">
          <div className="flex justify-end">
            {activeTab === "partners" && (
            <Button className="text-white" onClick={() => {
              setSelectedFleetOwner(undefined)
              setSelectedPartner(undefined)
              setSelectedUser(undefined)
              setSelectedBusinessRenter(undefined)
              setSelectedReferralAgent(undefined)
              setShowCreate(true);
            }}>
              Add Partner
            </Button>)}
            {activeTab === 'referral-agents' && <Button
              className="text-white"
              onClick={() => navigate('/admin/users/referral/commission-ledgers')}
            >
              View commission ledgers
            </Button>}
          </div>
          <Tabs
            defaultActiveKey={activeTab}
            items={items}
            tabBarExtraContent={SearchUsers}
            onChange={(key) => {
              setSelectedFleetOwner(undefined)
              setSelectedPartner(undefined)
              setSelectedUser(undefined)
              setSelectedBusinessRenter(undefined)
              setSelectedReferralAgent(undefined)
              setShowCreate(false);
              sessionStorage.setItem('active-user-tab', key);
              setActiveTab(key as ActiveTab);
            }}
          />
        </div>
      </div>
      {selectedUser && <ManageUser handleClosePanel={handleClosePanel} selectedUser={selectedUser} />}
      {selectedFleetOwner && <ManageFleetOwners handleClosePanel={() => setSelectedFleetOwner(undefined)} selectedUser={selectedFleetOwner} />}
      {selectedPartner && <ManagePartner
        handleClosePanel={() => setSelectedPartner(undefined)}
        selectedUser={selectedPartner}
        onSubmit={mutatePartner}
      />}
      {selectedBusinessRenter && <ManageBusinessRenter
        handleClosePanel={() => setSelectedBusinessRenter(undefined)}
        selectedUser={selectedBusinessRenter}
      />}
      {selectedReferralAgent && <ManageReferralAgent
        handleClosePanel={() => setSelectedReferralAgent(undefined)}
        selectedUser={selectedReferralAgent}
        onSubmit={mutateReferralAgents}
      />}
      {showCreate && <CreatePartner onClose={() => setShowCreate(false)} onSubmit={mutatePartner} />}
    </div>
  );
}